import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Link, Skeleton, Typography } from '@mui/material';
import RouterIcon from '@mui/icons-material/Router';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LoadingSkeleton from './LoadingSkeleton';

const TricolorBar = ({ online = 0, warning = 0, offline = 0 }) => {
  const total = online + warning + offline;
  if (total === 0) {
    return (
      <Box sx={{ width: '100%', height: 6, borderRadius: 3, bgcolor: 'action.disabledBackground' }} />
    );
  }
  const pctOnline = (online / total) * 100;
  const pctWarning = (warning / total) * 100;
  const pctOffline = (offline / total) * 100;
  return (
    <Box sx={{ width: '100%', height: 6, borderRadius: 3, overflow: 'hidden', display: 'flex' }}>
      {pctOnline > 0 && <Box sx={{ width: `${pctOnline}%`, bgcolor: 'success.main' }} />}
      {pctWarning > 0 && <Box sx={{ width: `${pctWarning}%`, bgcolor: 'warning.main' }} />}
      {pctOffline > 0 && <Box sx={{ width: `${pctOffline}%`, bgcolor: 'error.main' }} />}
    </Box>
  );
};

TricolorBar.propTypes = {
  online: PropTypes.number,
  warning: PropTypes.number,
  offline: PropTypes.number,
};

const StatusDot = ({ color, label, count = 0 }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}>
    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
    <Typography variant="caption" color="text.secondary">
      {label} ({count})
    </Typography>
  </Box>
);

StatusDot.propTypes = {
  color: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  count: PropTypes.number,
};

const DeviceStatusSkeleton = () => (
  <Box sx={{ width: '100%' }}>
    <Box sx={{ py: '2px' }}>
      <Skeleton variant="text" width={90} height={36} animation="wave" sx={{ borderRadius: 1 }} />
    </Box>
    <Box sx={{ mt: 1.5, mb: 1 }}>
      <Skeleton variant="rounded" width="100%" height={6} animation="wave" sx={{ borderRadius: 3 }} />
    </Box>
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', my: 0.5 }}>
      <Skeleton variant="rounded" width={60} height={16} animation="wave" />
      <Skeleton variant="rounded" width={60} height={16} animation="wave" />
      <Skeleton variant="rounded" width={60} height={16} animation="wave" />
    </Box>
    <Box sx={{ mt: 1.25, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
      <Skeleton variant="text" width={140} height={18} animation="wave" />
    </Box>
  </Box>
);

const DeviceStatusPanel = ({
  online = 0,
  warning = 0,
  offline = 0,
  total,
  unresolvedEventsCount = 0,
  onViewAll,
  onViewEvents,
  loading = false,
}) => {
  const { t } = useTranslation();
  const totalDevices = total ?? online + warning + offline;

  return (
    <Box>
      {/* Header row: icon + title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <Box sx={{ color: 'text.secondary', display: 'flex', fontSize: 16 }}>
          <RouterIcon fontSize="inherit" />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {t('dashboard:deviceStatus.title')}
        </Typography>
      </Box>

      <LoadingSkeleton loading={loading} height={140} customSkeleton={<DeviceStatusSkeleton />}>
        {/* Value */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <Typography variant="display" color="text.primary">
            {online}<Typography component="span" variant="body2" color="text.disabled"> / {totalDevices}</Typography>
          </Typography>
          {onViewAll && (
            <Link
              component="button"
              variant="caption"
              onClick={onViewAll}
              underline="hover"
              sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 500 }}
            >
              {t('dashboard:deviceStatus.viewAll')}
            </Link>
          )}
        </Box>

        {/* Tricolor bar */}
        <Box sx={{ mt: 1.5, mb: 1 }}>
          <TricolorBar online={online} warning={warning} offline={offline} />
        </Box>

        {/* Inline status dots */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <StatusDot color="success.main" label={t('dashboard:deviceStatus.online')} count={online} />
          <StatusDot color="warning.main" label={t('dashboard:deviceStatus.warning')} count={warning} />
          <StatusDot color="error.main" label={t('dashboard:deviceStatus.offline')} count={offline} />
        </Box>

        {/* Unresolved events footer row */}
        <Box
          sx={{
            mt: 1.25,
            pt: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            onClick={onViewEvents || onViewAll}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              color: unresolvedEventsCount > 0 ? 'error.main' : 'text.secondary',
              fontSize: '0.75rem',
              fontWeight: unresolvedEventsCount > 0 ? 600 : 500,
              '&:hover': { opacity: 0.85 },
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 14, color: unresolvedEventsCount > 0 ? 'error.main' : 'text.disabled' }} />
            <span>
              {unresolvedEventsCount > 0
                ? `${unresolvedEventsCount} ${unresolvedEventsCount === 1 ? t('dashboard:events.pendingSingle', 'evento por resolver') : t('dashboard:events.pendingPlural', 'eventos por resolver')}`
                : t('dashboard:events.noPending', 'Sem eventos por resolver')}
            </span>
          </Box>
        </Box>
      </LoadingSkeleton>
    </Box>
  );
};

DeviceStatusPanel.propTypes = {
  online: PropTypes.number,
  warning: PropTypes.number,
  offline: PropTypes.number,
  total: PropTypes.number,
  unresolvedEventsCount: PropTypes.number,
  onViewAll: PropTypes.func,
  onViewEvents: PropTypes.func,
  loading: PropTypes.bool,
};

export default DeviceStatusPanel;
